import { CutlistPartType } from '@cutr/constants/cutlist';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useLeadDetails } from '@/api/account';
import { useMaterialGroupErrorStore, usePartsErrorStore } from '@/api/errors';
import {
  getAgentPartNestingFields,
  useMaterialGroups,
  useMaterialGroupState,
  useSaveAllGroupChanges,
  useUnsavedGroups,
} from '@/api/materialsGroup';
import {
  computeCutlistNestingHash,
  useMixedNestingStore,
} from '@/api/mixedNesting';
import { useNestingStatusStore } from '@/api/nesting';
import { useCutlistParts, useCutlistState } from '@/api/store';
import { AgentNestingBlock } from '@/blocks/AgentNestingBlock';
import { AgentQuoteHeader } from '@/blocks/AgentOrderHeader';
import {
  CutlistEmailPreview,
  hasEmailData,
} from '@/blocks/CutlistEmailPreview';
import { useOwnerModalStore } from '@/blocks/CutlistOwnerButton';
import { AgentQuotingPartTable } from '@/interfaces/AgentQuotingPartTable';
import { useRunNesting } from '@/interfaces/Nesting';
import { Options } from '@/interfaces/PartTableCommon/Components';
import { useAiSuggestionWarnings } from '@/interfaces/PartTableCommon/useAiSuggestionChecks';
import { QuotingMaterialGroupTabNav } from '@/interfaces/QuotingMaterialGroupTabNav';
import AnchorLink from '@/primitives/AnchorLink';
import { Button } from '@/primitives/Button';
import { ArrowLeft, Icon, Loader } from '@/primitives/Icons';
import { CutlistEmailLoaderType } from '@/queries/loaders';
import { useIsMutatingMaterialGroup } from '@/queries/materialGroup';
import { isStarterTier } from '@/theme';

import styles from './AgentQuoteParts.module.css';

export const AgentQuoteParts = () => {
  const emailData = useLoaderData() as CutlistEmailLoaderType;
  const hasEmail = hasEmailData(emailData);

  return (
    <>
      <AgentQuoteHeader />
      <SubHeader />
      {hasEmail && <CutlistEmailPreview />}
      <div className="flexAlign opposites">
        <QuotingMaterialGroupTabNav />
        {isStarterTier() ? <StarterNextButton /> : <NextButton />}
      </div>
      <AgentQuotingPartTable />
      {isStarterTier() ? <AgentNestingBlock /> : <NestingHooks />}
    </>
  );
};

const NextButton = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [saveAllMaterialGroups] = useSaveAllGroupChanges();
  const navigate = useNavigate();
  const { email } = useLeadDetails();
  const { openModal: openOwnerModal } = useOwnerModalStore();
  const isMutatingMaterialGroup = useIsMutatingMaterialGroup();
  const nestingStatus = useNestingStatusStore((state) => state.status);
  const isNestingCompleted = Object.values(nestingStatus).every(
    (s) => s === 'completed'
  );
  const parts = useCutlistState((state) => state.parts);

  const { materialGroupErrors } = useMaterialGroupErrorStore();
  const { partErrors } = usePartsErrorStore();
  const aiSuggestionWarnings = useAiSuggestionWarnings();

  const isCTADisabled = isMutatingMaterialGroup;

  const navigateToPrice = async () => {
    if (!id) return;
    try {
      await saveAllMaterialGroups();
      navigate(`/admin/cutlists/orders/${id}/quote/price`);
    } catch (error) {
      toast.error(t('common.errors.saveOrder') as string);
    }
  };

  const onAction = async () => {
    if (!email) {
      openOwnerModal(
        t('agent.setCutlistOwnership.ownerRequiredForPricePage'),
        navigateToPrice
      );
      return;
    }

    if (materialGroupErrors.length > 0) {
      jumpToGroup(materialGroupErrors[0].materialGroupId);
      toast.error(t('agent.errors.groupError'));
      return;
    }

    if (partErrors.length > 0) {
      const part = parts?.find((p) => p.id === partErrors[0].partItemId);
      if (part) {
        jumpToGroup(part.groupId, part.id);
        toast.error(t('agent.errors.partError'));
      }
      return;
    }

    if (aiSuggestionWarnings.length > 0) {
      jumpToGroup(aiSuggestionWarnings[0].materialGroupId);
      toast.warning(t('agent.warnings.core1'));
      return;
    }

    if (!id || !isNestingCompleted) return;

    navigateToPrice();
  };

  if (!isNestingCompleted) {
    return (
      <AnchorLink anchor="nesting-parts" className={styles.anchorLink}>
        <Button
          style={{
            // @ts-ignore
            '--button-bkg': 'var(--agent-accent)',
          }}
          disabled={isCTADisabled}
        >
          <span style={{ textDecoration: 'none' }}>
            {t('agent.quoteFlow.nestingParts')}
          </span>
          <Icon icon={<Loader />} size={2.5} />
        </Button>
      </AnchorLink>
    );
  }

  return (
    <Button
      style={{
        width: 'auto',
      }}
      onClick={onAction}
      disabled={isMutatingMaterialGroup}
    >
      {t('agent.quoteFlow.toPriceCallToAction')}
      <Icon
        icon={<ArrowLeft />}
        style={{ transform: 'rotate(180deg)' }}
        size={2.5}
      />
    </Button>
  );
};

const StarterNextButton = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [saveAllMaterialGroups] = useSaveAllGroupChanges();
  const navigate = useNavigate();
  const { email } = useLeadDetails();
  const { openModal: openOwnerModal } = useOwnerModalStore();
  const isMutatingMaterialGroup = useIsMutatingMaterialGroup();
  const groups = useMaterialGroups();
  const nestingParts = useCutlistParts();
  const mixedNestingState = useMixedNestingStore((state) => state.nestingState);
  const mixedSnapshotHash = useMixedNestingStore((state) => state.snapshotHash);
  const liveHash = computeCutlistNestingHash(groups, nestingParts);
  const nestingDirty =
    mixedSnapshotHash !== null && liveHash !== mixedSnapshotHash;
  const isNestingCompleted = mixedNestingState === 'done' && !nestingDirty;
  const isComputing = mixedNestingState === 'computing';
  const hasUnsavedChanges = useUnsavedGroups().length > 0;
  const parts = useCutlistState((state) => state.parts);

  const { materialGroupErrors } = useMaterialGroupErrorStore();
  const { partErrors } = usePartsErrorStore();
  const aiSuggestionWarnings = useAiSuggestionWarnings();

  const navigateToPrice = async () => {
    if (!id) return;
    try {
      await saveAllMaterialGroups();
      navigate(`/admin/cutlists/orders/${id}/quote/price`);
    } catch (error) {
      toast.error(t('common.errors.saveOrder') as string);
    }
  };

  const onAction = async () => {
    if (!email) {
      openOwnerModal(
        t('agent.setCutlistOwnership.ownerRequiredForPricePage'),
        navigateToPrice
      );
      return;
    }

    if (materialGroupErrors.length > 0) {
      jumpToGroup(materialGroupErrors[0].materialGroupId);
      toast.error(t('agent.errors.groupError'));
      return;
    }

    if (partErrors.length > 0) {
      const part = parts?.find((p) => p.id === partErrors[0].partItemId);
      if (part) {
        jumpToGroup(part.groupId, part.id);
        toast.error(t('agent.errors.partError'));
      }
      return;
    }

    if (aiSuggestionWarnings.length > 0) {
      jumpToGroup(aiSuggestionWarnings[0].materialGroupId);
      toast.warning(t('agent.warnings.core1'));
      return;
    }

    if (!id || !isNestingCompleted || hasUnsavedChanges) return;

    navigateToPrice();
  };

  if (isComputing) {
    return (
      <AnchorLink anchor="nesting-parts" className={styles.anchorLink}>
        <Button
          style={{
            // @ts-ignore
            '--button-bkg': 'var(--agent-accent)',
          }}
          disabled={isMutatingMaterialGroup}
        >
          <span style={{ textDecoration: 'none' }}>
            {t('agent.quoteFlow.nestingParts')}
          </span>
          <Icon icon={<Loader />} size={2.5} />
        </Button>
      </AnchorLink>
    );
  }

  return (
    <Button
      style={{
        width: 'auto',
      }}
      onClick={onAction}
      disabled={
        isMutatingMaterialGroup || hasUnsavedChanges || !isNestingCompleted
      }
    >
      {t('agent.quoteFlow.toPriceCallToAction')}
      <Icon
        icon={<ArrowLeft />}
        style={{ transform: 'rotate(180deg)' }}
        size={2.5}
      />
    </Button>
  );
};

export const SubHeader = () => {
  return <Options />;
};

const NestingHooks = () => {
  useRunNesting(getAgentPartNestingFields);
  return null;
};

export const jumpToGroup = (groupId: string, partId?: string) => {
  const { setActive } = useMaterialGroupState.getState();
  const parts = useCutlistState.getState().parts;

  const jump = () => {
    setActive(groupId);

    if (!partId) {
      return;
    }

    Promise.resolve().then(() => {
      const panelType = parts?.find((p) => p.id === partId)?.partType;
      if (!panelType) return;

      const typePrefix: Record<CutlistPartType, string> = {
        panel: 'panelRow',
        strip: 'stripRow',
        sheet: 'sheetRow',
        beam: 'beamRow',
      };

      const prefix = typePrefix[panelType];
      const panelRow = document.getElementById(`${prefix}-${partId}`);
      if (panelRow) {
        panelRow.scrollIntoView({ behavior: 'smooth' });
      }
    });
  };

  jump();
};
